import { format, parseISO, isValid } from "date-fns";
import React, { useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IVDMDashboardContent } from "redux/app/vdmdashboard/@types";
import { IStore } from "redux/store";

const safeFormat = (
	value: string | null | undefined,
	pattern: string,
	fallback = "Invalid time",
): string => {
	if (!value) return fallback;
	const parsed = parseISO(value);
	if (!isValid(parsed)) return fallback;
	return format(parsed, pattern);
};

const transformRow = (row: IVDMDashboardContent["stopagges"][0]) => {
	return {
		location: "Not available",
		time: row.delayDuration,
		date: safeFormat(row?.sourceSelectedDate, "yyyy-MM-dd", "Invalid"),
		startTime: safeFormat(row?.startDateTime, "HH:mm"),
		endTime: safeFormat(row?.endDateTime, "HH:mm"),
		reason: row?.reason || "Not Specified",
		dalayType: row?.delayType,
		completeComment: row?.remarks ?? "",
		comments:
			row?.remarks?.substring(0, 60) +
			(row?.remarks && row?.remarks.length > 60 ? "..." : ""),
	};
};

type OwnProps = {
	stoppages?: IVDMDashboardContent["stopagges"];
};
type StoppagesTableProps = PropsFromRedux & OwnProps;
function StoppagesTable(props: StoppagesTableProps) {
	const { stoppages } = props;

	const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
		null,
	);
	const toggleExpand = (id: string | number) => {
		setExpandedRowId((prevId) => (prevId === id ? null : id));
	};

	const rowsData = React.useMemo(() => {
		if (!stoppages || !Array.isArray(stoppages)) return [];
		return stoppages.map((row) => transformRow(row));
	}, [stoppages]);

	if (rowsData?.length === 0) {
		return (
			<div className="p-6 text-center text-gray-500 bg-white border border-gray-200 rounded">
				No stoppage data found.
			</div>
		);
	}

	return (
		<div className="overflow-x-auto border border-b-none border-gray-300 rounded ws-scroll">
			<table className="min-w-full overflow-x-auto divide-y divide-gray-200">
				<thead className="bg-gray-50 text-gray-600">
					<tr>
						<th className="px-3 py-2 text-left text-sm font-medium w-full whitespace-nowrap">
							Location
						</th>
						<th className="px-3 py-2 text-sm font-medium w-full whitespace-nowrap">
							Time
						</th>
						<th className="px-3 py-2 text-sm font-medium w-full whitespace-nowrap">
							Date
						</th>
						<th className="px-3 py-2 text-sm font-medium w-full whitespace-nowrap">
							Start time
						</th>
						<th className="px-3 py-2 text-sm font-medium w-full whitespace-nowrap">
							End time
						</th>
						<th className="px-3 py-2 text-sm font-medium w-full whitespace-nowrap">
							Reason
						</th>
						<th className="px-3 py-2 text-sm font-medium w-full whitespace-nowrap">
							Delay Type
						</th>
						<th className="px-3 py-2 text-sm font-medium w-full whitespace-nowrap">
							Comments
						</th>
					</tr>
				</thead>
				<tbody className="bg-white">
					{rowsData?.map((row, idx) => {
						const isExpanded = expandedRowId === idx;
						const hasLongComment = (row?.comments?.length ?? 0) > 30;

						return (
							<tr key={idx} className="border-b last:border-0">
								<td className="px-3 py-2 text-sm whitespace-nowrap">
									{row?.location}
								</td>
								<td className="px-3 py-2 text-sm whitespace-nowrap">
									{row?.time}
								</td>
								<td className="px-3 py-2 text-sm whitespace-nowrap">
									{row?.date}
								</td>
								<td className="px-3 py-2 text-sm whitespace-nowrap">
									{row?.startTime}
								</td>
								<td className="px-3 py-2 text-sm whitespace-nowrap">
									{row?.endTime}
								</td>
								<td className="px-3 py-2 text-sm whitespace-nowrap">
									{row?.reason}
								</td>
								<td className="px-3 py-2 text-sm whitespace-nowrap">
									{row?.dalayType}
								</td>
								<td className="px-3 py-2 text-sm whitespace-nowrap">
									<span
										title={row?.completeComment}
										className={`block transition-all duration-200 ${
											isExpanded && expandedRowId === idx
												? "whitespace-normal break-words w-full"
												: "truncate max-w-[300px] 2xl:max-w-[400px]"
										}`}
									>
										{row?.completeComment ?? ""}
									</span>

									{hasLongComment && (
										<button
											type="button"
											onClick={() => toggleExpand(idx)}
											className="text-blue-600 hover:text-blue-800 text-xs font-medium self-start transition-colors"
										>
											{isExpanded ? "Show less" : "Show more"}
										</button>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(StoppagesTable);
